import * as BABYLON from "babylonjs"
import { Global } from "../../classes/global"
import { WebXRHitTest } from "babylonjs"

var _plane: BABYLON.Mesh

export class DialPicker {
	static _onAngleChanged: (newAngle: number) => void

	public static create(
		scene: BABYLON.Scene,
		xr: BABYLON.WebXRDefaultExperience,
		id: string,
		position: [number, number, number],
		size: number, // Assuming this is the diameter of the dial for consistency
		colour: string,
		accentColour: string,
		onAngleChanged: (newAngle: number) => void,
		grabbable: boolean = false,
		visibility: boolean,
		parentID?: string // New parameter for parent ID
	): void {
		// Create a plane to act as the ground for the dial picker
		const plane = BABYLON.MeshBuilder.CreateGround(
			`${id}_ground`,
			{ width: size, height: size, subdivisions: 2 },
			scene
		)
		plane.position = new BABYLON.Vector3(position[0], position[1], position[2])
		plane.rotation.x = (Math.PI / 2) * 3
		_plane = plane
		const textureResolution = 1024 // or match 'size' more closely if needed
		const textureGround = new BABYLON.DynamicTexture(
			`${id}_dynamicTexture`,
			{ width: textureResolution, height: textureResolution },
			scene,
			false
		)

		const context = textureGround.getContext()
		const materialGround = new BABYLON.StandardMaterial(`${id}_Mat`, scene)
		materialGround.diffuseTexture = textureGround
		materialGround.diffuseTexture.hasAlpha = true
		materialGround.backFaceCulling = false
		materialGround.useAlphaFromDiffuseTexture = true
		plane.material = materialGround

		const dialCenter = { x: textureResolution / 2, y: textureResolution / 2 }
		this.drawDial(
			context as any,
			dialCenter.x,
			dialCenter.y,
			0,
			textureGround,
			colour,
			accentColour
		) // Initial angle

		let dialInteraction = false

		// Enable interaction with the dial
		plane.actionManager = new BABYLON.ActionManager(scene)
		//const inverseWorldMatrix = plane.getWorldMatrix().invert()
		plane.actionManager.registerAction(
			new BABYLON.ExecuteCodeAction(
				BABYLON.ActionManager.OnPickTrigger,
				(evt) => {
					console.log("picked")
					const pickedPointWorld = evt.additionalData.pickedPoint

					// Get the inverse of the plane's world matrix
					//const inverseWorldMatrix = plane.getWorldMatrix()

					// Transform the picked point to the plane's local space
					const pickedPointLocal = BABYLON.Vector3.TransformCoordinates(
						pickedPointWorld,
						plane.getWorldMatrix().clone().invert()
					)

					// Adjusting the angle for the canvas drawing in the local coordinate system
					const angleRadians =
						Math.atan2(pickedPointLocal.z, pickedPointLocal.x) + Math.PI / 2
					let angleDegrees = angleRadians * (180 / Math.PI)

					// Normalize the angle to 0 - 360 degrees
					angleDegrees = (angleDegrees + 360) % 360

					// Now use the angleDegrees for your logic
					onAngleChanged(angleDegrees)

					this.drawDial(
						context as any,
						dialCenter.x,
						dialCenter.y,
						angleRadians,
						textureGround,
						colour,
						accentColour
					)

					// Update your drawing logic as necessary
				}
			)
		)

		var interactionObserver: BABYLON.Nullable<BABYLON.Observer<BABYLON.Scene>> =
			null

		interactionObserver = scene.onBeforeRenderObservable.add(() => {
			// is it pressed?
			if (Global.dialInteraction) {
				// evt is the event parameter that contains data about the pointer event.
				// pickResult contains data about the object under the pointer when the event occurred.

				// Check if the click hits the ground object
				console.log("Pointer down at position:")
				if (Global.dialHit) {
					var pickedPointWorld = Global.dialPickedPoint
					console.log("Pointer down at position:", pickedPointWorld)

					const pickedPointLocal = BABYLON.Vector3.TransformCoordinates(
						pickedPointWorld!,
						plane.getWorldMatrix().clone().invert()
					)

					// Adjusting the angle for the canvas drawing in the local coordinate system
					const angleRadians =
						Math.atan2(pickedPointLocal.z, pickedPointLocal.x) + Math.PI / 2
					let angleDegrees = angleRadians * (180 / Math.PI)

					// Normalize the angle to 0 - 360 degrees
					angleDegrees = (angleDegrees + 360) % 360

					// Now use the angleDegrees for your logic
					onAngleChanged(angleDegrees)

					DialPicker.drawDial(
						context as any,
						dialCenter.x,
						dialCenter.y,
						angleRadians,
						textureGround,
						colour,
						accentColour
					)

					// You can use pickedPoint which is a Vector3 to know where on the 3D space the pointer was clicked
				}
			}
		})

		if (parentID) {
			const parentMesh = plane._scene!.getMeshByID(parentID) as BABYLON.Mesh
			if (parentMesh) {
				plane.parent = parentMesh
			}
		}

		if (grabbable) {
			// Instead of PointerDragBehavior, use SixDofDragBehavior for 3D dragging
			const dragBehavior = new BABYLON.SixDofDragBehavior()
			plane.addBehavior(dragBehavior)

			dragBehavior.onDragStartObservable.add(() => {
				console.log("3D dragging started")
			})
			// No onDragObservable here, SixDofDragBehavior does not provide continuous updates like PointerDragBehavior
			dragBehavior.onDragEndObservable.add(() => {
				console.log("3D dragging ended")
			})
		}
		plane.isVisible = visibility
		plane.renderingGroupId = 2
	}

	private static drawDial(
		context: CanvasRenderingContext2D,
		centerX: number,
		centerY: number,
		angle: number,
		texture: BABYLON.DynamicTexture,
		colour: string,
		accentColour: string
	): void {
		const textureResolution = texture.getSize().width // Assuming square texture for simplicity
		context.clearRect(0, 0, textureResolution, textureResolution) // Clear the canvas
		// Draw the dial
		context.beginPath()
		context.arc(centerX, centerY, 100 * 4, 0, 2 * Math.PI) // Consider making the radius adjustable
		context.strokeStyle = colour
		context.lineWidth = 7 * 4
		context.stroke()

		// Calculate and draw the needle
		const needleLength = 90 * 4
		const needleOffset = 9 * 4
		const endX =
			centerX - (needleLength - needleOffset) * Math.cos(angle + Math.PI / 2) // Adjust for orientation
		const endY =
			centerY + (needleLength - needleOffset) * Math.sin(angle + Math.PI / 2)

		// Draw the needle's tip as a ring
		context.beginPath()
		context.arc(endX, endY, 7 * 4, 0, 2 * Math.PI) // Radius of the ring
		context.strokeStyle = accentColour
		context.lineWidth = 5 * 4
		context.stroke()
		context.restore()
		texture.update() // Apply changes to the texture
	}
}
